import api from "../auth/api";
import type {
  GetStockAnalysesQuery,
  GetStockAnalysesResponse,
  CreateStockAnalysisRequest,
  CreateStockAnalysisResponse,
  UpdateStockAnalysisRequest,
  UpdateStockAnalysisResponse,
  GetStockAnalysisResponse,
  SearchStocksResponse,
  CreateCombinedStockAnalysisRequest,
  CreateCombinedStockAnalysisResponse,
  GetStockSectorsResponse,
  GetLatestStockAnalysesResponse,
  GetSignalStatsResponse,
  SignalStatsWindow,
} from "./stock-analysis.type";

// Get stock analyses list with pagination and filters
export const getStockAnalyses = async (
  query: GetStockAnalysesQuery = {}
): Promise<GetStockAnalysesResponse> => {
  const response = await api.get<GetStockAnalysesResponse>("/stock-analyses", {
    params: query,
  });
  return response.data;
};

// Get stock analysis by ID
export const getStockAnalysisById = async (
  id: string
): Promise<GetStockAnalysisResponse> => {
  const response = await api.get<GetStockAnalysisResponse>(
    `/stock-analyses/${id}`
  );
  return response.data;
};

// Create new stock analysis
export const createStockAnalysis = async (
  data: CreateStockAnalysisRequest
): Promise<CreateStockAnalysisResponse> => {
  const response = await api.post<CreateStockAnalysisResponse>(
    "/stock-analyses",
    data
  );
  return response.data;
};

// Update stock analysis by ID
export const updateStockAnalysis = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateStockAnalysisRequest;
}): Promise<UpdateStockAnalysisResponse> => {
  const response = await api.put<UpdateStockAnalysisResponse>(
    `/stock-analyses/${id}`,
    data
  );
  return response.data;
};

// Delete stock analysis by ID
export const deleteStockAnalysis = async (id: string): Promise<void> => {
  await api.delete(`/stock-analyses/${id}`);
};

// Search stocks by query
export const searchStocks = async (
  query: string
): Promise<SearchStocksResponse> => {
  const response = await api.get<SearchStocksResponse>(
    "/stock-analyses/search",
    {
      params: { symbol: query },
    }
  );
  return response.data;
};

// Create combined stock analysis and report
export const createCombinedStockAnalysis = async (
  data: CreateCombinedStockAnalysisRequest
): Promise<CreateCombinedStockAnalysisResponse> => {
  const response = await api.post<CreateCombinedStockAnalysisResponse>(
    "/stock-analyses/combined",
    data
  );
  return response.data;
};

// Get stock sectors
export const getStockSectors = async (): Promise<GetStockSectorsResponse> => {
  const response = await api.get<GetStockSectorsResponse>(
    "/stock-analyses/sectors"
  );
  return response.data;
};

// Get latest stock analyses (public endpoint)
export const getLatestStockAnalyses =
  async (): Promise<GetLatestStockAnalysesResponse> => {
    const response = await api.get<GetLatestStockAnalysesResponse>(
      "/public/stock-analyses/latest"
    );
    return response.data;
  };

// Get signal track record stats (public endpoint)
export const getSignalStats = async (
  window: SignalStatsWindow = "12m"
): Promise<GetSignalStatsResponse> => {
  const response = await api.get<GetSignalStatsResponse>(
    "/public/stock-analyses/stats",
    { params: { window } }
  );
  return response.data;
};
